/**
 * Keeps optional attendance context consistent before it is persisted with an
 * external contact event. Empty values are stored as null so reports can group
 * them predictably without retaining meaningless whitespace.
 */
/**
 * @param {{ exame?: string, convenio?: string }} [context]
 */
export function normalizeContactContext(context = {}) {
  const { exame, convenio } = context;
  const normalize = (value) => {
    const normalized = String(value || '').trim();
    return normalized || null;
  };

  return { exame: normalize(exame), convenio: normalize(convenio) };
}

export function canStartPhoneCall(context) {
  return Boolean(normalizeContactContext(context).exame);
}

/**
 * Runs one tracking action before the browser is asked to open the dialer.
 * Dependencies are passed in so this ordering remains straightforward to test.
 */
export function recordAndStartPhoneCall({ href, label, context, track, navigate }) {
  if (!canStartPhoneCall(context)) return false;
  track(href, label, normalizeContactContext(context));
  navigate(href);
  return true;
}

/**
 * Mirrors the segmented dashboard grouping for deterministic UI-level tests.
 * Legacy events without an exam intentionally stay out of this report.
 */
export function aggregateContactInteractions(events = []) {
  const rows = new Map();

  for (const event of events) {
    const { exame, convenio } = normalizeContactContext(event);
    if (!exame || !['whatsapp', 'telefone'].includes(event?.tipo)) continue;

    const convenioLabel = convenio || 'Não informado';
    const key = `${exame}\u0000${convenioLabel}`;
    const row = rows.get(key) || { exame, convenio: convenioLabel, whatsapp: 0, ligacoes: 0, total: 0 };
    if (event.tipo === 'whatsapp') row.whatsapp += 1;
    if (event.tipo === 'telefone') row.ligacoes += 1;
    row.total += 1;
    rows.set(key, row);
  }

  return [...rows.values()].sort((a, b) => (
    b.total - a.total || a.exame.localeCompare(b.exame, 'pt-BR') || a.convenio.localeCompare(b.convenio, 'pt-BR')
  ));
}
